export interface PageInfo {
  name: string;
  category: string;
  studioId?: string;
  metadata?: Record<string, any>;
}

export class PageMapper {
  // Main static routes
  private static exactRoutes: Record<string, PageInfo> = {
    // === MAIN SECTIONS ===
    '/myStudios': { name: 'My Studios', category: 'Studio Management' },
    '/myClients': { name: 'My Clients', category: 'Client Management' },

    // === STAFF ===
    '/my-teachers': { name: 'My Teachers', category: 'Staff Management' },

    // === SALES FUNNEL ===
    '/new-funnel': { name: 'Create New Funnel', category: 'Sales Funnel' },
    '/pending-client/extra-field/setting/': { name: 'Funnel Extra Fields Settings', category: 'Sales Funnel' },

    // === PRICING & SUBSCRIPTIONS ===
    '/rate-plans': { name: 'Rate Plan Templates', category: 'Subscription Management' },
    '/new-rate-plans': { name: 'Create Rate Plan Template', category: 'Subscription Management' },
    '/billing-tags': { name: 'Lesson Types', category: 'Subscription Management' },
    '/matrix': { name: 'Price Matrix', category: 'Subscription Management' },
    '/payment-setting': { name: 'Payment Methods', category: 'Subscription Management' },
    '/client-rate-plans': { name: 'Subscription Report', category: 'Analytics & Reports' },

    // === FINANCES ===
    '/finances': { name: 'Studio Finances', category: 'Financial Management' },

    // === MESSAGING & NOTIFICATIONS ===
    '/messaging': { name: 'Messaging Center', category: 'Communication' },

    // === EDUCATION & SUPPORT ===
    '/forum': { name: 'Forum', category: 'Education & Support' },
    '/ai': { name: 'AI Assistant', category: 'Education & Support' },

    // === SETTINGS ===
    '/organization-settings': { name: 'Organization Settings', category: 'Settings' },
    '/myAccount': { name: 'My Account', category: 'Settings' },
    '/change-password': { name: 'Change Password', category: 'Settings' },

    // === NOTIFICATIONS ===
    '/business-notification': { name: 'Business Notifications', category: 'Notifications' },

    // === AUTHENTICATION ===
    '/login': { name: 'Login', category: 'Authentication' },
    '/forget-password': { name: 'Forget Password', category: 'Authentication' },
  };

  // Studio routes (most important)
  private static studioRoutes: Array<{
    pattern: RegExp;
    name: string;
    category: string;
    getMetadata?: (match: RegExpMatchArray) => Record<string, any>;
  }> = [
    // 🔥 STUDIO CALENDAR
    {
      pattern: /^\/studio\/id\/([^\/]+)\/registrations$/,
      name: 'Studio Calendar',
      category: 'Studio Calendar',
      getMetadata: (match) => ({
        studioId: match[1],
        pageType: 'calendar',
        isHighPriority: true
      })
    },

    // 🔥 SPECIFIC LESSON IN CALENDAR
    {
      pattern: /^\/studio\/id\/([^\/]+)\/registrations\/([^\/]+)$/,
      name: 'Lesson Page (Edit/Confirm)',
      category: 'Studio Calendar',
      getMetadata: (match) => ({
        studioId: match[1],
        groupId: match[2],
        pageType: 'lesson_detail',
        lessonDate: match[2].slice(-12), // Last 12 characters - date/time
        isHighPriority: true
      })
    },

    // === STUDIO PAGES ===
    {
      pattern: /^\/studio\/id\/([^\/]+)\/qr-scanner$/,
      name: 'QR Scanner',
      category: 'Studio Tools',
      getMetadata: (match) => ({ studioId: match[1], pageType: 'qr_scanner' })
    },
    {
      pattern: /^\/studio\/id\/([^\/]+)\/school$/,
      name: 'School Journal',
      category: 'Studio Management',
      getMetadata: (match) => ({ studioId: match[1], pageType: 'school_journal' })
    },
    {
      pattern: /^\/studio\/([^\/]+)\/homeworks$/,
      name: 'Homework Management',
      category: 'Studio Education',
      getMetadata: (match) => ({ studioId: match[1], pageType: 'homework' })
    },
    {
      pattern: /^\/studio\/id\/([^\/]+)\/dashboard$/,
      name: 'Studio Statistics',
      category: 'Analytics & Reports',
      getMetadata: (match) => ({ studioId: match[1], pageType: 'studio_stats' })
    },
    {
      pattern: /^\/studio\/id\/([^\/]+)\/edit$/,
      name: 'Studio Settings',
      category: 'Studio Management',
      getMetadata: (match) => ({ studioId: match[1], pageType: 'studio_settings' })
    },
    {
      pattern: /^\/studio\/id\/([^\/]+)\/rooms$/,
      name: 'Studio Rooms',
      category: 'Studio Management',
      getMetadata: (match) => ({ studioId: match[1], pageType: 'rooms' })
    },

    // === SCHEDULE & COURSES ===
    {
      pattern: /^\/studio\/id\/([^\/]+)\/schedule$/,
      name: 'Static Schedule',
      category: 'Schedule Management',
      getMetadata: (match) => ({ studioId: match[1], pageType: 'static_schedule' })
    },
    {
      pattern: /^\/studio\/id\/([^\/]+)\/schedule\/courses$/,
      name: 'Studio Courses & Groups',
      category: 'Course Management',
      getMetadata: (match) => ({ studioId: match[1], pageType: 'courses_list' })
    },
    {
      pattern: /^\/studio\/id\/([^\/]+)\/schedule\/course$/,
      name: 'Create New Course',
      category: 'Course Management',
      getMetadata: (match) => ({ studioId: match[1], pageType: 'course_create' })
    },
    {
      pattern: /^\/studio\/id\/([^\/]+)\/schedule\/course\/([^\/]+)$/,
      name: 'Edit Course',
      category: 'Course Management',
      getMetadata: (match) => ({
        studioId: match[1],
        courseId: match[2],
        pageType: 'course_edit'
      })
    },
    {
      pattern: /^\/studio\/id\/([^\/]+)\/schedule\/course\/([^\/]+)\/class$/,
      name: 'Add Lesson to Course',
      category: 'Course Management',
      getMetadata: (match) => ({
        studioId: match[1],
        courseId: match[2],
        pageType: 'lesson_create'
      })
    },
    {
      pattern: /^\/studio\/id\/([^\/]+)\/schedule\/deleted-course\/([^\/]+)$/,
      name: 'View Deleted Course',
      category: 'Course Management',
      getMetadata: (match) => ({
        studioId: match[1],
        courseId: match[2],
        pageType: 'course_deleted'
      })
    },
  ];

  // Dynamic routes
  private static dynamicRoutes: Array<{
    pattern: RegExp;
    name: string;
    category: string;
    getMetadata?: (match: RegExpMatchArray) => Record<string, any>;
  }> = [
    // === CLIENT MANAGEMENT ===
    {
      pattern: /^\/myClients\/([^\/]+)$/,
      name: 'Client Profile',
      category: 'Client Management',
      getMetadata: (match) => ({ clientId: match[1], pageType: 'client_detail' })
    },
    {
      pattern: /^\/myClients\/([^\/]+)\/attendance$/,
      name: 'Client Attendance History',
      category: 'Client Management',
      getMetadata: (match) => ({ clientId: match[1], pageType: 'client_attendance' })
    },

    // === FINANCES & BILLING ===
    {
      pattern: /^\/billing\/([^\/]+)$/,
      name: 'Unpaid Attendances',
      category: 'Financial Management',
      getMetadata: (match) => ({ studioId: match[1], pageType: 'unpaid_billing' })
    },

    // === SALES FUNNEL ===
    {
      pattern: /^\/pending-clients\/([^\/]+)$/,
      name: 'Sales Funnel Leads',
      category: 'Sales Funnel',
      getMetadata: (match) => ({ funnelId: match[1], pageType: 'funnel_leads' })
    },
    {
      pattern: /^\/funnel\/([^\/]+)$/,
      name: 'Funnel Settings',
      category: 'Sales Funnel',
      getMetadata: (match) => ({ funnelId: match[1], pageType: 'funnel_settings' })
    },
    {
      pattern: /^\/pending-clients\/([^\/]+)\/([^\/]+)$/,
      name: 'Lead Profile',
      category: 'Sales Funnel',
      getMetadata: (match) => ({
        funnelId: match[1],
        leadId: match[2],
        pageType: 'lead_detail'
      })
    },

    // === STAFF ===
    {
      pattern: /^\/teacher\/([^\/]+)$/,
      name: 'Employee Profile',
      category: 'Staff Management',
      getMetadata: (match) => ({ teacherId: match[1], pageType: 'employee_detail' })
    },
    {
      pattern: /^\/invite-teacher\/([^\/]+)\/([^\/]+)$/,
      name: 'Employee Invitation',
      category: 'Staff Management',
      getMetadata: (match) => ({
        studioId: match[1],
        teacherId: match[2],
        pageType: 'employee_invite'
      })
    },

    // === MESSAGING ===
    {
      pattern: /^\/message-batch\/([^\/]+)$/,
      name: 'Message Details',
      category: 'Communication',
      getMetadata: (match) => ({ messageId: match[1], pageType: 'message_detail' })
    },

    // === SUBSCRIPTIONS ===
    {
      pattern: /^\/rate-plans\/([^\/]+)$/,
      name: 'Edit Rate Plan Template',
      category: 'Subscription Management',
      getMetadata: (match) => ({ ratePlanId: match[1], pageType: 'rate_plan_edit' })
    },

    // === FORUM ===
    {
      pattern: /^\/forum\/task\/([^\/]+)$/,
      name: 'Forum Topic',
      category: 'Education & Support',
      getMetadata: (match) => ({ topicId: match[1], pageType: 'forum_topic' })
    },

    // === REGISTRATION ===
    {
      pattern: /^\/apply(?:\/([^\/]+))?$/,
      name: 'Registration',
      category: 'Authentication',
      getMetadata: (match) => ({
        language: match[1] || 'default',
        pageType: 'registration'
      })
    },

    // === COMPLEX CLIENT ROUTES ===
    {
      pattern: /^\/myClients\/([^\/]+)\?ratePlanToViewId=([^&]+)&ratePlanInfoToViewId=([^&]+)$/,
      name: 'Client RatePlan Details',
      category: 'Client Management',
      getMetadata: (match) => ({
        clientId: match[1],
        ratePlanInstanceId: match[2],
        ratePlanTemplateId: match[3],
        pageType: 'client_ratePlan_detail'
      })
    },
  ];

  public static getPageInfo(pathname: string): PageInfo {
    // Check exact matches
    if (this.exactRoutes[pathname]) {
      return this.exactRoutes[pathname];
    }

    // 🔥 Priority to studio routes
    for (const route of this.studioRoutes) {
      const match = pathname.match(route.pattern);
      if (match) {
        const metadata = route.getMetadata ? route.getMetadata(match) : {};
        return {
          name: route.name,
          category: route.category,
          studioId: metadata.studioId,
          metadata
        };
      }
    }

    // Check dynamic routes
    for (const route of this.dynamicRoutes) {
      const match = pathname.match(route.pattern);
      if (match) {
        const metadata = route.getMetadata ? route.getMetadata(match) : {};
        return {
          name: route.name,
          category: route.category,
          metadata
        };
      }
    }

    // Default fallback
    return {
      name: pathname.replace(/^\//, '').replace(/\//g, ' ') || 'Home',
      category: 'Other'
    };
  }

  public static getPageName(pathname: string): string {
    return this.getPageInfo(pathname).name;
  }

  public static getPageCategory(pathname: string): string {
    return this.getPageInfo(pathname).category;
  }

  // Special methods
  public static isStudioCalendar(pathname: string): boolean {
    return /^\/studio\/id\/[^\/]+\/registrations$/.test(pathname);
  }

  public static isLessonPage(pathname: string): boolean {
    return /^\/studio\/id\/[^\/]+\/registrations\/[^\/]+$/.test(pathname);
  }

  public static getStudioIdFromPath(pathname: string): string | null {
    const match = pathname.match(/^\/studio\/id\/([^\/]+)/);
    return match ? match[1] : null;
  }

  public static getLessonInfo(pathname: string): { studioId: string; groupId: string; lessonDate: string } | null {
    const match = pathname.match(/^\/studio\/id\/([^\/]+)\/registrations\/([^\/]+)$/);
    if (match) {
      return {
        studioId: match[1],
        groupId: match[2],
        lessonDate: match[2].slice(-12) // Last 12 characters
      };
    }
    return null;
  }

  // Get all categories for analytics
  public static getAllCategories(): string[] {
    const categories = new Set<string>();

    // Static routes
    Object.values(this.exactRoutes).forEach(route => categories.add(route.category));

    // Studio routes
    this.studioRoutes.forEach(route => categories.add(route.category));

    // Dynamic routes
    this.dynamicRoutes.forEach(route => categories.add(route.category));

    return Array.from(categories).sort();
  }
}
